import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { CopyIconButton, SectionCard, monoValueStyle } from "./displayPrimitives";
import { fetchLesson, isEndpointMissing } from "./evaluatorApi";

// New-tab CTA target from a CCSS relationship's IM lesson. Shows the lesson
// description, learning goals, its LC list, and every cooldown problem for the
// lesson — the gated content SMEs need in one place (SPEC §10.9).
export const LessonDetailView = () => {
  const { lessonId } = useParams();
  const { loading: authLoading } = useAuthUserData();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef(null);

  // Cooldown/description content is curriculum HTML with inline LaTeX (\(..\),
  // \$). dangerouslySetInnerHTML doesn't trigger MathJax, so typeset the
  // container once the lesson loads (converts \(..\) and \$ -> $).
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const mj = window.MathJax;
      try {
        if (mj.typesetPromise) mj.typesetPromise();
        else mj.typeset();
      } catch (e) {
        /* ignore */
      }
    };
    // Whole-page typeset, gated on MathJax 4 startup (the prior element-scoped,
    // ref-guarded version never fired). Poll until MathJax has loaded.
    const ready = () => {
      const mj = typeof window !== "undefined" ? window.MathJax : null;
      if (!mj || !(mj.typesetPromise || mj.typeset)) return false;
      if (mj.startup && mj.startup.promise && mj.startup.promise.then) {
        mj.startup.promise.then(run);
      } else {
        run();
      }
      return true;
    };
    if (ready()) return () => { cancelled = true; };
    let n = 0;
    const iv = setInterval(() => {
      if (cancelled || ready() || ++n > 20) clearInterval(iv);
    }, 150);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [lesson]);

  useEffect(() => {
    let cancelled = false;
    if (!lessonId) return undefined;
    setLoading(true);
    setPending(false);
    setError("");
    fetchLesson(lessonId)
      .then((res) => {
        if (!cancelled) setLesson(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isEndpointMissing(e)) setPending(true);
        else setError("Failed to load lesson.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  if (authLoading || loading) {
    return (
      <div style={{ padding: "48px", textAlign: "center" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
      </div>
    );
  }
  if (pending) {
    return (
      <div style={{ padding: "32px", color: "#b06a00", fontStyle: "italic" }}>
        /admin/evaluator/lessons/{lessonId} endpoint not available yet — pending backend.
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: "32px", color: "#a33" }}>{error}</div>;
  }
  if (!lesson) {
    return <div style={{ padding: "32px", color: "#999" }}>Lesson not found.</div>;
  }

  const cooldowns = lesson.cooldown_problems || [];

  return (
    <div ref={containerRef} style={{ maxWidth: "900px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <h4 style={{ marginBottom: "4px" }}>
          {lesson.lesson_code ? `${lesson.lesson_code} — ` : ""}
          {lesson.lesson_name || `Unit ${lesson.unit_name} / Lesson ${lesson.lesson_name}`}
        </h4>
        {(lesson.lesson_code || lesson.lesson_name) && (
          <div style={{ fontSize: "0.85em", color: "#888", marginBottom: "4px" }}>
            Unit {lesson.unit_name} / Lesson {lesson.lesson_name}
            {lesson.grade_level ? ` · Grade ${lesson.grade_level}` : ""}
          </div>
        )}
        <span style={monoValueStyle}>{lessonId}</span>
        <CopyIconButton value={lessonId} title="Copy lesson ID" />
      </div>

      <SectionCard title="Lesson description">
        {lesson.description ? (
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{lesson.description}</div>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        )}
      </SectionCard>

      <SectionCard title="Learning goals">
        {lesson.learning_goals ? (
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{lesson.learning_goals}</div>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        )}
      </SectionCard>

      <SectionCard title="Learning Components">
        {(lesson.learning_components || []).length === 0 ? (
          <span style={{ color: "#aaa" }}>—</span>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            {lesson.learning_components.map((lc) => (
              <li key={lc.lc_id} style={{ marginBottom: "4px" }}>
                <span style={monoValueStyle}>{lc.lc_id}</span>
                <CopyIconButton value={lc.lc_id} title="Copy LC ID" />
                {" — "}
                {lc.lc_name}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title={`Cooldown problems (${cooldowns.length})`}>
        {cooldowns.length === 0 ? (
          <span style={{ color: "#aaa" }}>No cooldown problems.</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {cooldowns.map((cd, i) => (
              <div
                key={i}
                style={{ border: "1px solid #eee", borderRadius: "6px", padding: "12px", background: "#fafbfc" }}
              >
                {cd.name && <div style={{ fontWeight: 600, marginBottom: "6px" }}>{cd.name}</div>}
                {/* Cooldown content is curriculum HTML from the gated CZI flat files. */}
                <div dangerouslySetInnerHTML={{ __html: cd.content_html || "" }} />
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};
